import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faStop, faVolumeUp, faSpinner } from '@fortawesome/free-solid-svg-icons'; // Added faVolumeUp and faSpinner
import './DrumMachinePython.css'; // Ensure this is the correct CSS file

const DRUM_TYPES = ['kick', 'snare', 'hihat'];
const NUM_STEPS = 16;
const INITIAL_BPM = 120;

// Definition of the initial Drum Machine pattern
const pattern_01 = {
    kick: [1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0],
    snare: [0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 1],
    hihat: [1, 0, 1, 0, 1, 1, 1, 0, 1, 0, 1, 1, 1, 0, 1, 0],
};


function DrumMachine({ socket: propSocket }) {
    const [connectionStatus, setConnectionStatus] = useState('Connecting...');
    const [isConnected, setIsConnected] = useState(false);
    const [isReadyForPlayback, setIsReadyForPlayback] = useState(false);
    const [userMessage, setUserMessage] = useState('');
    const messageTimeoutRef = useRef(null);

    // NEW STATE: Controls the visibility of the initial audio activation overlay
    const [showActivationOverlay, setShowActivationOverlay] = useState(true);
    // NEW STATE: Tracks if sounds are currently being loaded after activation click
    const [isLoadingInitialSounds, setIsLoadingInitialSounds] = useState(false);


    const [sequence, setSequence] = useState(pattern_01);
    const [bpm, setBpm] = useState(INITIAL_BPM);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentVisualStep, setCurrentVisualStep] = useState(-1);

    const audioContext = useRef(null);
    const audioBuffers = useRef({});

    const schedulerRef = useRef(null);
    const nextNoteTimeRef = useRef(0.0);
    const lookahead = 0.1;
    const scheduleInterval = 25;

    const initAudioContext = useCallback(() => {
        if (!audioContext.current) {
            audioContext.current = new (window.AudioContext || window.webkitAudioContext)();
            console.log('AudioContext created (state:', audioContext.current.state + ')');
        }
        return audioContext.current;
    }, []);

    const playSound = useCallback((drumType, time) => {
        const audioCtx = audioContext.current;
        const buffer = audioBuffers.current[drumType];

        if (!audioCtx || audioCtx.state !== 'running' || !buffer) {
            return;
        }

        const source = audioCtx.createBufferSource();
        source.buffer = buffer;
        source.connect(audioCtx.destination);
        source.start(time);

        const drumPadButton = document.getElementById(`pad-${drumType}`);
        if (drumPadButton) {
            drumPadButton.classList.add('active');
            setTimeout(() => {
                drumPadButton.classList.remove('active');
            }, 150);
        }
    }, []);

    const showUserMessage = useCallback((message, duration = 3000) => {
        if (messageTimeoutRef.current) {
            clearTimeout(messageTimeoutRef.current);
        }
        setUserMessage(message);
        messageTimeoutRef.current = setTimeout(() => {
            setUserMessage('');
            messageTimeoutRef.current = null;
        }, duration);
    }, []);


    // Effetto per gestire la connessione Socket.IO e il caricamento iniziale dei suoni
    useEffect(() => {
        if (!propSocket) {
            setConnectionStatus('No socket provided by wrapper.');
            setIsConnected(false);
            // showActivationOverlay will remain true, signaling an issue
            return;
        }

        initAudioContext(); // Initialize AudioContext

        // Set initial connection status based on socket state
        setIsConnected(propSocket.connected);
        setConnectionStatus(propSocket.connected ? `Connected (ID: ${propSocket.id})` : 'Connecting...');
        // If already connected on mount, might already be able to load sounds
        if (propSocket.connected) {
             // We don't activate here. Activation is through the overlay button click.
        }

        propSocket.on('connect', () => {
            setConnectionStatus(`Connected (ID: ${propSocket.id})`);
            setIsConnected(true);
            console.log('DrumMachine: Connected to backend.');
            // When connected, if we are in the initial loading phase, trigger sound requests
            if (isLoadingInitialSounds && !isReadyForPlayback) {
                console.log("Socket reconnected during initial load. Requesting all drum sounds...");
                DRUM_TYPES.forEach(type => {
                    propSocket.emit('trigger_drum', { type: type });
                });
            }
        });

        propSocket.on('disconnect', (reason) => {
            setConnectionStatus(`Disconnected (${reason})`);
            setIsConnected(false);
            setIsReadyForPlayback(false);
            setIsLoadingInitialSounds(false); // Reset loading state
            audioBuffers.current = {};
            setIsPlaying(false);
            setCurrentVisualStep(-1);
            // Do NOT show overlay again on disconnect, only on initial mount.
            // showActivationOverlay is managed by isReadyForPlayback now.
            showUserMessage('Disconnected from backend. Sounds may not be available.', 5000);
        });

        propSocket.on('connect_error', (error) => {
            setConnectionStatus(`Connection Error: ${error.message}`);
            setIsConnected(false);
            setIsReadyForPlayback(false);
            setIsLoadingInitialSounds(false); // Reset loading state
            console.error('DrumMachine: Socket connection error:', error);
            showUserMessage(`Connection error: ${error.message}. Please try again later.`, 5000);
        });

        propSocket.on('audio_data', (data) => {
            console.log(`DrumMachine: Received audio data for ${data.type}`);
            const audioCtx = audioContext.current;
            if (!audioCtx) {
                console.error("AudioContext not initialized on audio_data reception.");
                return;
            }

            const base64String = data.wav_base64;
            if (!base64String) {
                console.error("No Base64 WAV data received from backend for", data.type);
                return;
            }

            const binaryString = atob(base64String);
            const len = binaryString.length;
            const bytes = new Uint8Array(len);
            for (let i = 0; i < len; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            const arrayBuffer = bytes.buffer;

            audioCtx.decodeAudioData(arrayBuffer)
                .then(buffer => {
                    audioBuffers.current[data.type] = buffer;
                    console.log(`DrumMachine: Buffered ${data.type} sound.`);

                    const allBuffered = DRUM_TYPES.every(type => audioBuffers.current[type] !== undefined);
                    if (allBuffered) {
                        setIsReadyForPlayback(true);
                        setIsLoadingInitialSounds(false); // Finished initial loading
                        // showUserMessage('All sounds loaded. The drum machine is ready!', 4000); // Overlay disappears, so no need for this message
                        console.log("All drum sounds buffered. Ready for playback!");
                        // IMPORTANT: HIDE THE OVERLAY HERE
                        setShowActivationOverlay(false);
                    }
                })
                .catch(e => {
                    console.error(`DrumMachine: Error decoding audio data for ${data.type}:`, e);
                    showUserMessage(`Error loading ${data.type} sound.`, 5000);
                    // If decoding fails for any critical sound, the app might not be fully ready.
                    // Keep `isReadyForPlayback` false.
                });
        });

        propSocket.on('error', (err) => {
            console.error("DrumMachine Socket error:", err);
            setConnectionStatus(`Error: ${err.message}`);
            setIsReadyForPlayback(false);
            setIsLoadingInitialSounds(false); // Reset loading state
            showUserMessage(`Backend error: ${err.message}.`, 5000);
        });

        return () => {
            console.log('DrumMachine: Cleaning up socket listeners.');
            propSocket.off('connect');
            propSocket.off('disconnect');
            propSocket.off('connect_error');
            propSocket.off('audio_data');
            propSocket.off('error');
            if (messageTimeoutRef.current) {
                clearTimeout(messageTimeoutRef.current);
            }
        };
    }, [propSocket, initAudioContext, isLoadingInitialSounds, isReadyForPlayback, showUserMessage]); // Added new states to dependencies


    const playbackLoop = useCallback(() => {
        const audioCtx = audioContext.current;
        if (!audioCtx || !isPlaying || !isReadyForPlayback || audioCtx.state !== 'running') return;

        const secondsPerBeat = 60.0 / bpm;
        const secondsPerStep = secondsPerBeat / 4;

        while (nextNoteTimeRef.current < audioCtx.currentTime + lookahead) {
            let stepToSchedule = nextNoteTimeRef.currentStepInternal || 0;

            DRUM_TYPES.forEach(drumType => {
                if (sequence[drumType] && sequence[drumType][stepToSchedule]) {
                    playSound(drumType, nextNoteTimeRef.current);
                }
            });

            const visualUpdateDelay = (nextNoteTimeRef.current - audioCtx.currentTime) * 1000;
            setTimeout(() => {
                setCurrentVisualStep(stepToSchedule);
            }, Math.max(0, visualUpdateDelay));

            nextNoteTimeRef.currentStepInternal = (stepToSchedule + 1) % NUM_STEPS;
            nextNoteTimeRef.current += secondsPerStep;
        }
    }, [bpm, isPlaying, sequence, playSound, isReadyForPlayback]);

    useEffect(() => {
        if (!audioContext.current || !isReadyForPlayback) {
            return;
        }

        if (isPlaying) {
            if (audioContext.current.state === 'suspended') {
                 audioContext.current.resume().then(() => {
                     console.log("AudioContext resumed by isPlaying useEffect (secondary check).");
                 }).catch(e => {
                     console.error("Failed to resume AudioContext in useEffect (secondary):", e);
                     setIsPlaying(false);
                     showUserMessage('Could not resume AudioContext. Please interact with the page.', 5000);
                 });
            }
            
            if (schedulerRef.current === null) {
                nextNoteTimeRef.current = audioContext.current.currentTime;
                nextNoteTimeRef.currentStepInternal = 0;
                setCurrentVisualStep(-1);
            }
            
            schedulerRef.current = setInterval(playbackLoop, scheduleInterval);
            console.log("Sequencer started.");
        } else {
            if (schedulerRef.current) {
                clearInterval(schedulerRef.current);
                schedulerRef.current = null;
            }
            setCurrentVisualStep(-1);
            nextNoteTimeRef.currentStepInternal = 0;
            console.log("Sequencer stopped/paused.");
        }

        return () => {
            if (schedulerRef.current) {
                clearInterval(schedulerRef.current);
            }
        };
    }, [isPlaying, playbackLoop, isReadyForPlayback, showUserMessage]);

    const toggleNote = useCallback((drumType, stepIndex) => {
        setSequence(prevSequence => {
            const newSequence = { ...prevSequence };
            newSequence[drumType] = [...prevSequence[drumType]];
            newSequence[drumType][stepIndex] = !newSequence[drumType][stepIndex];
            return newSequence;
        });
    }, []);

    const triggerDrum = useCallback(async (drumType) => {
        const audioCtx = initAudioContext();
        if (audioCtx.state === 'suspended') {
            try {
                await audioCtx.resume();
                console.log("AudioContext resumed by drum pad click.");
            } catch (e) {
                console.error("Failed to resume AudioContext on drum pad click:", e);
                showUserMessage('Cannot play sound: AudioContext suspended. Try clicking on pads or Play button.', 5000);
                return;
            }
        }

        const buffer = audioBuffers.current[drumType];
        if (propSocket && propSocket.connected) {
            if (buffer) {
                if (audioCtx.state === 'running') {
                    playSound(drumType, audioCtx.currentTime);
                    console.log(`DrumMachine: Played buffered ${drumType}`);
                } else {
                    console.warn(`DrumMachine: AudioContext not running, cannot play ${drumType} immediately.`);
                    showUserMessage('AudioContext is not active. Cannot play sound.', 5000);
                }
            } else {
                propSocket.emit('trigger_drum', { type: drumType });
                console.log(`DrumMachine: Requesting ${drumType} from backend (not yet buffered).`);
                showUserMessage(`Loading ${drumType} sound. Please wait...`, 3000);
            }
        } else {
            console.warn('DrumMachine: Socket not connected, cannot trigger drum.');
            setConnectionStatus('Disconnected - Please check backend');
            showUserMessage('Not connected to backend. Cannot play.', 5000);
        }
    }, [propSocket, initAudioContext, playSound, showUserMessage]);

    // NEW FUNCTION: Handles the click on the initial activation button
    const handleActivateAudio = useCallback(async () => {
        setIsLoadingInitialSounds(true); // Start loading indicator
        const audioCtx = initAudioContext(); // Ensure context is created

        if (propSocket && !propSocket.connected) {
            console.warn("Socket not connected. Cannot request sounds yet. Will retry on 'connect'.");
            showUserMessage("Connecting to backend... please wait.", 5000);
            // Do not proceed with resume/emit here if socket is not connected.
            // The 'connect' event listener will handle requesting sounds.
            return; 
        }

        if (audioCtx.state === 'suspended') {
            try {
                await audioCtx.resume();
                console.log("AudioContext resumed by activation button.");
                // After resume, request all sounds from backend
                DRUM_TYPES.forEach(type => {
                    propSocket.emit('trigger_drum', { type: type });
                });
                showUserMessage("Loading sounds... please wait.", 5000);
            } catch (e) {
                console.error("Failed to resume AudioContext on activation click:", e);
                showUserMessage('Failed to activate audio. Please refresh and try again.', 7000);
                setIsLoadingInitialSounds(false); // Stop loading indicator on error
            }
        } else {
            // If context is already running (e.g., desktop, or already resumed somehow)
            // Immediately request all sounds to ensure they are buffered
            DRUM_TYPES.forEach(type => {
                propSocket.emit('trigger_drum', { type: type });
            });
            showUserMessage("Loading sounds... please wait.", 5000);
        }
    }, [initAudioContext, propSocket, showUserMessage]); // Added propSocket and showUserMessage to dependencies


    // Main render logic
    return (
        <div className="drum-machine-container python">
            {showActivationOverlay && (
                <div className="audio-activation-overlay">
                    {isLoadingInitialSounds ? (
                        <div className="loading-spinner-container">
                            <FontAwesomeIcon icon={faSpinner} spin size="3x" className="spinner-icon" />
                            <p className="loading-text">Loading Sounds...</p>
                        </div>
                    ) : (
                        <button className="activate-audio-button" onClick={handleActivateAudio}>
                            <FontAwesomeIcon icon={faVolumeUp} size="2x" />
                            <span>Tap to Load Sounds</span>
                        </button>
                    )}
                    {!isConnected && !isLoadingInitialSounds && (
                         <p className="overlay-status-message">Connecting to backend...</p>
                    )}
                    {isConnected && !isLoadingInitialSounds && (
                         <p className="overlay-status-message">Tap button to activate audio and load sounds.</p>
                    )}
                    {/* Display message from userMessage state if relevant */}
                    {userMessage && <p className="overlay-error-message">{userMessage}</p>}
                </div>
            )}

            {/* Main Drum Machine UI - Render only when overlay is hidden */}
            {!showActivationOverlay && (
                <>
                    <h1 className="drum-machine-title">Drum Machine</h1>

                    <div className="drum-pads">
                        {DRUM_TYPES.map(type => (
                            <button
                                key={type}
                                id={`pad-${type}`}
                                className={`drum-pad-button ${type}`}
                                onClick={() => triggerDrum(type)}
                                disabled={!isReadyForPlayback} // Disable pads if sounds not ready
                            >
                                {type.toUpperCase()}
                            </button>
                        ))}
                    </div>

                    <div className="sequencer-grid-container">
                        {DRUM_TYPES.map(drumType => (
                            <div key={drumType} className="sequencer-row">
                                <div className="instrument-label">{drumType.toUpperCase()}</div>
                                <div className="steps-container">
                                    {Array.from({ length: NUM_STEPS }).map((_, stepIndex) => {
                                        const isActiveNote = sequence[drumType][stepIndex];
                                        const isCurrentStep = isPlaying && currentVisualStep === stepIndex;

                                        return (
                                            <div
                                                key={`${drumType}-${stepIndex}`}
                                                className={`step-cell ${isActiveNote ? 'active-note' : ''} ${isCurrentStep ? 'current-step-highlight' : ''}`}
                                                onClick={() => toggleNote(drumType, stepIndex)}
                                            >
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="controls-bar">
                        <button
                            id="playPauseButton"
                            className={`play-pause-button ${isPlaying ? 'pause' : 'play'}`}
                            onClick={async () => {
                                const audioCtx = initAudioContext();
                                // This resume is primarily for robustness; the main resume happens on overlay activation
                                if (audioCtx.state === 'suspended') {
                                    try {
                                        await audioCtx.resume();
                                        console.log("AudioContext resumed by Play/Pause button (secondary).");
                                    } catch (e) {
                                        console.error("Failed to resume AudioContext on Play/Pause click (secondary):", e);
                                        showUserMessage('Error: Audio activation failed. Please refresh the page.', 7000);
                                        return;
                                    }
                                }
                                if (isConnected && isReadyForPlayback) {
                                    setIsPlaying(prev => !prev);
                                } else {
                                    let message = '';
                                    if (!isConnected) {
                                        message = 'Not connected to the backend. Please wait.';
                                    } else if (!isReadyForPlayback) {
                                        message = 'Sounds are still loading. Please wait for completion.';
                                    } else {
                                        message = 'To play, ensure you are connected and sounds are loaded.';
                                    }
                                    showUserMessage(message, 5000);
                                    console.warn("Cannot start playback: Not connected or sounds not ready.");
                                }
                            }}
                            disabled={!isConnected || !isReadyForPlayback}
                        >
                            <FontAwesomeIcon icon={isPlaying ? faStop : faPlay} size="2x" />
                        </button>

                        <div className="bpm-control">
                            <label htmlFor="bpmSlider">BPM:</label>
                            <input
                                type="range"
                                id="bpmSlider"
                                min="60"
                                max="200"
                                step="1"
                                value={bpm}
                                onChange={(e) => setBpm(parseInt(e.target.value))}
                            />
                            <span id="bpmValue" className="bpm-value">{bpm}</span>
                        </div>
                    </div>

                    <p id="connectionStatus" className={`connection-status ${isConnected ? 'connected' : 'disconnected'}`}>
                        Status: {connectionStatus} {isReadyForPlayback ? '(Sounds Ready)' : '(Loading Sounds...)'}
                    </p>
                </>
            )}

            {/* User messages are now part of the overlay or main UI */}
            {/* Keeping the user message display here outside the conditional render for flexibility */}
            {userMessage && !showActivationOverlay && ( // Only show if overlay is hidden
                <div className="user-message-container">
                    <p className="user-message">{userMessage}</p>
                </div>
            )}
        </div>
    );
}

export default DrumMachine;
